// utils/sorting.ts

import { IProductData } from '@/utils';

/**
 * Sorts data by display_color using a custom order.
 */
export const sortByDisplayColor = (
  data: Partial<IProductData>[],
  customOrder: string[]
): Partial<IProductData>[] => {
  return data.sort((a, b) => {
    const colorIndexA = customOrder.indexOf(a.display_color || '');
    const colorIndexB = customOrder.indexOf(b.display_color || '');

    if (colorIndexA !== -1 && colorIndexB !== -1) {
      return colorIndexA - colorIndexB; // Sort based on custom order
    } else if (colorIndexA !== -1) {
      return -1; // `a` is in custom order, comes first
    } else if (colorIndexB !== -1) {
      return 1; // `b` is in custom order, comes first
    }

    return 0; // Maintain existing order if not in custom order
  });
};

/**
 * Sorts data by year_generation and submodel.
 */
export const sortByYearGenerationAndSubmodel = (data: IProductData[], customOrder: string[]) => {
  return data.sort((a, b) => {
    // Only apply seat prioritization if products have display_set (seat covers)
    const hasDisplaySet = a.display_set || b.display_set;
    if (hasDisplaySet) {
      const seatTypePriority = ['Front Seat', 'Rear Seat', 'Full Set'];
      const getPriority = (displaySet: string | undefined) => {
        const index = seatTypePriority.indexOf(displaySet || '');
        return index !== -1 ? index : seatTypePriority.length;
      };

      const priorityA = getPriority(a.display_set);
      const priorityB = getPriority(b.display_set);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
    }

    // Sort by year_generation (latest first)
    const startYearA = a.year_generation
      ? parseInt(a.year_generation.split('-')[0], 10)
      : -Infinity;
    const startYearB = b.year_generation
      ? parseInt(b.year_generation.split('-')[0], 10)
      : -Infinity;

    if (startYearA !== startYearB) {
      return startYearB - startYearA;
    }

    // Sort by custom order of submodel1
    const indexA = customOrder.indexOf(a.submodel1 || '');
    const indexB = customOrder.indexOf(b.submodel1 || '');

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    } else if (indexA !== -1) {
      return -1;
    } else if (indexB !== -1) {
      return 1;
    }

    // Fallback to alphabetical sort by submodel1
    return (a.submodel1 || '').localeCompare(b.submodel1 || '');
  });
};
